import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import SwapVertIcon from '@mui/icons-material/SwapVertOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturnOutlined';
import { apiClient } from '@/api/axiosClient';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import DashboardPieChart from '@/components/dashboard/DashboardPieChart';
import TvKpiTile from '@/components/tvDashboard/TvKpiTile';
import TvTopList from '@/components/tvDashboard/TvTopList';
import { useCategoryColors } from '@/pages/dashboard/dashboardColors';
import { groupCountBy, groupSumBy } from '@/lib/dashboardAggregations';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type { CycleCountVarianceRow, ReturnNoteRegisterRow, StockAdjustmentRegisterRow } from '@/types/reports';

const STATUS_SLICE_COLORS = ['#0566d9', '#ffb951', '#4edea3', '#ba1a1a', '#6b6f76', '#3e5c8a'];

export default function AdjustmentsSlide() {
  const { t } = useTranslation('dashboard');
  const colors = useCategoryColors();
  const { data: processConfiguration } = useProcessConfiguration();
  // Mirrors AdjustmentsPanel.tsx's sub-flag gating — each sub-section tracks its own
  // independently-toggleable master switch.
  const stockAdjustmentEnabled = processConfiguration ? processConfiguration.stockAdjustmentEnabled : true;
  const cycleCountEnabled = processConfiguration ? processConfiguration.cycleCountEnabled : true;
  const returnNoteEnabled = processConfiguration ? processConfiguration.returnNoteEnabled : true;

  const { data: adjustments = [] } = useQuery({
    queryKey: ['reports', 'stock-adjustment-register'],
    queryFn: async () => (await apiClient.get<StockAdjustmentRegisterRow[]>('/reports/stock-adjustment-register')).data,
    refetchInterval: 60_000,
  });
  const { data: cycleCounts = [] } = useQuery({
    queryKey: ['reports', 'cycle-count-variance'],
    queryFn: async () => (await apiClient.get<CycleCountVarianceRow[]>('/reports/cycle-count-variance')).data,
    refetchInterval: 60_000,
  });
  const { data: returns = [] } = useQuery({
    queryKey: ['reports', 'return-note-register'],
    queryFn: async () => (await apiClient.get<ReturnNoteRegisterRow[]>('/reports/return-note-register')).data,
    refetchInterval: 60_000,
  });

  const totalAdjustments = useMemo(() => new Set(adjustments.map((r) => r.adjustmentId)).size, [adjustments]);
  const totalVariance = useMemo(() => adjustments.reduce((sum, r) => sum + Math.abs(r.variance), 0), [adjustments]);
  const totalCycleCounts = useMemo(() => new Set(cycleCounts.map((r) => r.cycleCountId)).size, [cycleCounts]);
  const totalReturns = useMemo(() => new Set(returns.map((r) => r.returnNoteId)).size, [returns]);

  const reasonDistribution = useMemo(() => groupCountBy(adjustments, (r) => r.reason), [adjustments]);
  const returnTypeDistribution = useMemo(() => groupCountBy(returns, (r) => r.returnType), [returns]);
  const topVariance = useMemo(
    () => groupSumBy(adjustments, (r) => `${r.materialCode} — ${r.materialName}`, (r) => Math.abs(r.variance)).slice(0, 5),
    [adjustments],
  );

  const kpiCount = (stockAdjustmentEnabled ? 2 : 0) + (cycleCountEnabled ? 1 : 0) + (returnNoteEnabled ? 1 : 0);
  const chartColumns = [stockAdjustmentEnabled, returnNoteEnabled].filter(Boolean).length === 2 ? '1fr 0.8fr 0.9fr' : '1.3fr 0.9fr';

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(kpiCount, 1)}, 1fr)`, gap: 2 }}>
        {stockAdjustmentEnabled && (
          <>
            <TvKpiTile label={t('adjustments.totalAdjustments')} value={totalAdjustments} icon={<TuneIcon />} accentColor={colors.adjustments} />
            <TvKpiTile label={t('adjustments.totalVariance')} value={totalVariance.toLocaleString()} icon={<SwapVertIcon />} accentColor={colors.adjustments} />
          </>
        )}
        {cycleCountEnabled && (
          <TvKpiTile label={t('adjustments.totalCycleCounts')} value={totalCycleCounts} icon={<FactCheckIcon />} accentColor={colors.adjustments} />
        )}
        {returnNoteEnabled && (
          <TvKpiTile label={t('adjustments.totalReturns')} value={totalReturns} icon={<KeyboardReturnIcon />} accentColor={colors.adjustments} />
        )}
      </Box>
      {(stockAdjustmentEnabled || returnNoteEnabled) && (
        <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: chartColumns, gap: 2, minHeight: 0 }}>
          {stockAdjustmentEnabled && (
            <ChartCard title={t('adjustments.reasonTitle')} height={420}>
              <DashboardBarChart
                layout="horizontal"
                categories={reasonDistribution.map((r) => r.key)}
                series={[{ label: t('adjustments.count'), data: reasonDistribution.map((r) => r.count), color: colors.adjustments }]}
                height={420}
                legendPosition="bottom"
              />
            </ChartCard>
          )}
          {returnNoteEnabled && (
            <ChartCard title={t('adjustments.returnTypeTitle')} height={420}>
              <DashboardPieChart
                data={returnTypeDistribution.map((r, i) => ({ label: r.key, value: r.count, color: STATUS_SLICE_COLORS[i % STATUS_SLICE_COLORS.length] }))}
                height={420}
                legendPosition="bottom"
              />
            </ChartCard>
          )}
          {stockAdjustmentEnabled && (
            <TvTopList title={t('adjustments.topVarianceTitle')} accentColor={colors.adjustments} items={topVariance.map((r) => ({ label: r.key, value: r.total }))} />
          )}
        </Box>
      )}
    </Box>
  );
}
